import { SupportedLocaleValues, LocaleDirections, type TextDirection } from '@/generated/core-enums'
import { en, type MessageId, type Messages } from './catalogue.en'
import { he } from './catalogue.he'
import { ru } from './catalogue.ru'

export const LOCALES = SupportedLocaleValues

export type Locale = (typeof LOCALES)[number]

export type Direction = TextDirection

export const DEFAULT_LOCALE: Locale = 'en'

const DIRECTIONS = LocaleDirections

const CATALOGUES: Record<Locale, Partial<Messages>> = {
  en,
  he,
  ru,
}

/**
 * THE LANGUAGES A CLINIC PUBLISHES IN used to be a narrower set than the ones it
 * works in, and this note is what is left of that distinction.
 *
 * A `ContentLanguage` type lived here while the seeded print forms existed only
 * in English and Hebrew: a Russian interface had to resolve to English before it
 * could pick a binding, because offering Russian would have printed a document
 * nobody authored. Both seeded forms now publish a Russian edition, so the print
 * flow is keyed by `Locale` and the crossing has nothing left to convert - an
 * identity function pretending to be a boundary is worse than no boundary, since
 * it reads as a rule while enforcing nothing.
 *
 * What did NOT widen is content a clinic AUTHORS: a protocol's text and a
 * service's display name are still the `{en, he}` pair the clinical publishing
 * contract names, so a Russian print form can carry Russian chrome around an
 * English service name. That boundary is the Protocol Editor's, which still
 * spells it out under this name; giving it a third language is a content-model
 * change and not a translation.
 */

/**
 * A language names ITSELF in any list that offers it, whatever the interface
 * language is. Shared, because the interface offers languages in more than one
 * place — switching the interface, and choosing what a document prints in.
 *
 * `Record<Locale, …>`: a locale added to `LOCALES` without an endonym is a
 * compile error here, not an option that renders as a blank line for the one
 * person who needed it.
 */
export const NATIVE_NAME_ID: Record<Locale, MessageId> = {
  en: 'language.nativeEn',
  he: 'language.nativeHe',
  ru: 'language.nativeRu',
}

/**
 * A language NAMED IN THE READER'S OWN language, which is the opposite
 * requirement to `NATIVE_NAME_ID` above and the reason the two id families
 * exist. This one answers "what language is this?" wherever the reader stays in
 * their current interface language: Patient preferences, dictation choices,
 * processing targets and stored language facts all share this owner. Endonyms
 * instead answer "which interface language do you want?" for someone who may
 * not be able to read the current one.
 */
export const LANGUAGE_NAME_ID: Record<Locale, MessageId> = {
  en: 'language.en',
  he: 'language.he',
  ru: 'language.ru',
}

/** Resolve a known interface locale's translated name; preserve unknown tags as data. */
export function languageNameId(value: string): MessageId | null {
  return isLocale(value) ? LANGUAGE_NAME_ID[value] : null
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

export function isDirection(value: unknown): value is Direction {
  return value === 'ltr' || value === 'rtl'
}

export function directionOf(locale: Locale): Direction {
  return DIRECTIONS[locale]
}

/**
 * RENDERING A MESSAGE ID, ALREADY BOUND TO A LOCALE.
 *
 * One name for the shape every surface passes around, because a module that
 * needs to translate something and is not a component takes this function as
 * an argument. Spelling the signature again at each of those modules is how
 * two of them end up disagreeing about whether params are allowed.
 */
export type Translate = (
  id: MessageId,
  params?: Readonly<Record<string, string | number>>,
) => string

/**
 * A VALUE OF UNKNOWN SCRIPT, SAFE TO PUT INSIDE A SENTENCE THE CATALOGUE WROTE.
 *
 * `bdi` is the isolation a rendered value gets, and it cannot be used here:
 * what goes into `{from}` is a string, not markup, and the sentence around it
 * comes back as one string too. The Unicode isolate pair is the same rule in
 * the only form a string has — FSI (U+2068) asks the bidi algorithm to resolve
 * the run inside it on its own, PDI (U+2069) hands the paragraph back.
 *
 * This matters exactly where a catalogue MIRRORS its template. `he`'s change
 * line says `{from} ← {to}` where `en` says `{from} → {to}`, so the phrase must
 * keep the workspace's direction while each value keeps its own. A `bdi` around
 * the whole rendered sentence does the opposite: it re-resolves the phrase from
 * the first strong character of the interpolated result, and one Latin-named
 * value lays the Hebrew line out left to right with the arrow still mirrored —
 * which says the change ran the other way.
 */
export function isolated(value: string): string {
  return `\u2068${value}\u2069`
}

/**
 * Resolve a message id for a locale, falling back to English when the locale
 * has no entry. English itself is complete by construction, so the returned
 * string is never a raw key and never text from a third locale.
 */
export function translate(
  locale: Locale,
  id: MessageId,
  params?: Readonly<Record<string, string | number>>,
): string {
  const runtimeId = id as string | undefined
  const template = runtimeId
    ? (CATALOGUES[locale][runtimeId as MessageId] ?? en[runtimeId as MessageId] ?? runtimeId)
    : (CATALOGUES[locale]['common.unknownValue'] ?? en['common.unknownValue'])
  if (!params) {
    return template
  }
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name]
    return value === undefined ? match : String(value)
  })
}

/**
 * Pick the starting locale from a browser language list. Only exact supported
 * tags and their base language are honoured; anything else lands on English.
 *
 * `ru-RU` resolves to Russian by its base language, the same way `he-IL` always
 * resolved to Hebrew. Nothing here is per-locale: a locale added to `LOCALES`
 * is negotiable the moment it is listed.
 */
export function negotiateLocale(preferred: readonly string[]): Locale {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0]
    if (isLocale(base)) {
      return base
    }
  }
  return DEFAULT_LOCALE
}
